import type { PortCallTypeRef } from '@gat-ship/types'

/**
 * Functional core — pure operations over `PortCallTypeRef[]`.
 *
 * Every function takes the current list (read-only) and returns a fresh value,
 * never mutating the input and never touching a ref, the network, or loading
 * state. The imperative shell (`usePortCallTypesDataSource`) owns the single
 * write point that assigns these results to the cache.
 */

/** Sort by `name` (ascending) via `localeCompare`. Returns a new array. */
export function sortPortCallTypesByName(
  items: readonly PortCallTypeRef[]
): PortCallTypeRef[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Insert or replace an entry by `id`, returning a new array sorted by name.
 * Read-only — never mutates the input.
 */
export function upsertPortCallType(
  items: readonly PortCallTypeRef[],
  item: PortCallTypeRef
): PortCallTypeRef[] {
  const exists = items.some((t) => t.id === item.id)
  const next = exists
    ? items.map((t) => (t.id === item.id ? item : t))
    : [...items, item]
  return sortPortCallTypesByName(next)
}

/** Drop the entry matching `id`. Returns a new array; never mutates input. */
export function removePortCallTypeById(
  items: readonly PortCallTypeRef[],
  id: string
): PortCallTypeRef[] {
  return items.filter((t) => t.id !== id)
}
